import * as d3 from 'd3';
import { TradeDataPoint, CandleDataPoint, MarketAnalysisData, SeriesVisibility, VolumeVisibility, AggregatedVolumePoint } from './types';

export interface TooltipLine {
    text: string;
    isHeader?: boolean;
    isSeparator?: boolean;
    isCompact?: boolean;
}

export interface FullSeriesTooltipResult {
    lines: TooltipLine[];
    nearbyTrade: TradeDataPoint | null;
}

// Find closest data point by timestamp
export function findClosestByTimestamp<T extends { timestamp: Date }>(
    date: Date,
    data: T[]
): T | null {
    if (!data || data.length === 0) return null;

    let closest = data[0];
    let minDistance = Math.abs(closest.timestamp.getTime() - date.getTime());

    for (const point of data) {
        const distance = Math.abs(point.timestamp.getTime() - date.getTime());
        if (distance < minDistance) {
            minDistance = distance;
            closest = point;
        }
    }

    return closest;
}

// Find data point at or before the given timestamp (floor behavior)
export function findDataPointAtOrBefore<T extends { timestamp: Date }>(
    date: Date,
    data: T[]
): T | null {
    if (!data || data.length === 0) return null;

    let result: T | null = null;
    let maxTimestamp = -Infinity;

    for (const point of data) {
        const ts = point.timestamp.getTime();
        if (ts <= date.getTime() && ts > maxTimestamp) {
            maxTimestamp = ts;
            result = point;
        }
    }

    return result ?? data[0];
}

// Find the candle that contains the given timestamp
export function findCandleAtTimestamp(
    date: Date,
    candles: CandleDataPoint[]
): CandleDataPoint | null {
    if (!candles || candles.length === 0) return null;

    for (const candle of candles) {
        if (date >= candle.timestamp && date < candle.closeTimestamp) {
            return candle;
        }
    }

    return findClosestByTimestamp(date, candles);
}

// Find the closest trade within a 2D proximity threshold
export function findNearbyTrade2D(
    mouseX: number,
    mouseY: number,
    trades: TradeDataPoint[],
    xScale: d3.ScaleTime<number, number>,
    yScale: d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number>,
    xThresholdPx: number,
    yThresholdPx: number
): TradeDataPoint | null {
    if (!trades || trades.length === 0) return null;

    let closestTrade: TradeDataPoint | null = null;
    let minNormalizedDistance = Infinity;

    for (const trade of trades) {
        const tradeX = xScale(trade.timestamp);
        const yOffset = trade.side === 'buy' ? 8 : -8;
        const tradeY = yScale(trade.price) + yOffset;

        const dx = Math.abs(mouseX - tradeX);
        const dy = Math.abs(mouseY - tradeY);

        if (dx <= xThresholdPx && dy <= yThresholdPx) {
            const normalizedDistance = Math.sqrt(
                Math.pow(dx / xThresholdPx, 2) + Math.pow(dy / yThresholdPx, 2)
            );

            if (normalizedDistance < minNormalizedDistance) {
                minNormalizedDistance = normalizedDistance;
                closestTrade = trade;
            }
        }
    }

    return closestTrade;
}

export function formatCurrency(value: number): string {
    if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(2)}M`;
    }
    if (value >= 1000) {
        return `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
}

export function formatPercent(value: number, useDecimal: boolean = false): string {
    return useDecimal ? value.toFixed(2) : `${value.toFixed(2)}%`;
}

export function getCandlePeriodMs(candles: CandleDataPoint[]): number | null {
    if (!candles || candles.length === 0) return null;
    const firstCandle = candles[0];
    return firstCandle.closeTimestamp.getTime() - firstCandle.openTimestamp.getTime();
}

export function formatTooltipDate(date: Date, candlePeriodMs: number | null): string {
    const dateStr = date.toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });

    if (!candlePeriodMs) {
        return `${dateStr} ${d3.timeFormat('%H:%M:%S')(date)}`;
    }

    const hours = candlePeriodMs / (1000 * 60 * 60);
    const minutes = candlePeriodMs / (1000 * 60);

    if (hours >= 24) {
        return dateStr;
    }
    if (hours >= 1 || minutes >= 1) {
        return `${dateStr} ${d3.timeFormat('%H:%M')(date)}`;
    }
    return `${dateStr} ${d3.timeFormat('%H:%M:%S')(date)}`;
}

export function buildFullSeriesTooltipLines(
    hoverDate: Date,
    analysisData: MarketAnalysisData,
    seriesVisibility: SeriesVisibility,
    volumeVisibility: VolumeVisibility,
    volumeData: AggregatedVolumePoint[],
    useDecimalScale: boolean,
    nearbyTrade: TradeDataPoint | null,
    candlePeriodMs: number | null
): FullSeriesTooltipResult {
    const lines: TooltipLine[] = [];

    lines.push({ text: formatTooltipDate(hoverDate, candlePeriodMs) });

    const bidAsk = findDataPointAtOrBefore(hoverDate, analysisData.bidAsk);
    const fairValue = findDataPointAtOrBefore(hoverDate, analysisData.fairValue);
    const candle = findCandleAtTimestamp(hoverDate, analysisData.candles || []);
    const volumePoint = findDataPointAtOrBefore(hoverDate, volumeData);

    // Bid/Ask Section
    if (seriesVisibility.bidLine || seriesVisibility.askLine) {
        lines.push({ text: '', isSeparator: true });
        lines.push({ text: '-- Bid/Ask --', isHeader: true });

        if (seriesVisibility.bidLine && bidAsk) {
            lines.push({ text: `Bid: ${formatPercent(bidAsk.bidPrice, useDecimalScale)}` });
        }
        if (seriesVisibility.askLine && bidAsk) {
            lines.push({ text: `Ask: ${formatPercent(bidAsk.askPrice, useDecimalScale)}` });
        }
        if (seriesVisibility.bidLine && seriesVisibility.askLine && bidAsk) {
            const spread = bidAsk.askPrice - bidAsk.bidPrice;
            lines.push({ text: `Spread: ${formatPercent(spread, useDecimalScale)}` });
        }
    }

    // Target Section
    if (seriesVisibility.fairValueLine || seriesVisibility.uncertaintyBand) {
        lines.push({ text: '', isSeparator: true });
        lines.push({ text: '-- Target --', isHeader: true });

        if (seriesVisibility.fairValueLine && fairValue) {
            lines.push({ text: `Fair Value: ${formatPercent(fairValue.probability, useDecimalScale)}` });
        }
        if (seriesVisibility.uncertaintyBand && fairValue) {
            lines.push({ text: `Uncertainty: +/-${formatPercent(fairValue.uncertainty, useDecimalScale)}` });
            lines.push({ text: `Range: ${formatPercent(fairValue.lowerBound, useDecimalScale)} - ${formatPercent(fairValue.upperBound, useDecimalScale)}` });
        }
    }

    // Candle Section
    if (seriesVisibility.candles && candle) {
        lines.push({ text: '', isSeparator: true });
        lines.push({ text: '-- Candle --', isHeader: true });

        const ohlc = `O: ${formatPercent(candle.open, useDecimalScale)}  H: ${formatPercent(candle.high, useDecimalScale)}  L: ${formatPercent(candle.low, useDecimalScale)}  C: ${formatPercent(candle.close, useDecimalScale)}`;
        lines.push({ text: ohlc, isCompact: true });

        lines.push({ text: `Volume: ${formatCurrency(candle.baseVolume)}` });
        lines.push({ text: `VWAP: ${formatPercent(candle.vwap, useDecimalScale)}` });
    }

    // Trade Section
    if (seriesVisibility.trades && nearbyTrade) {
        lines.push({ text: '', isSeparator: true });
        lines.push({ text: '-- Trade --', isHeader: true });

        lines.push({ text: `Time: ${formatTooltipDate(nearbyTrade.timestamp, null)}` });
        lines.push({ text: `Price: ${formatPercent(nearbyTrade.price, useDecimalScale)}` });
        lines.push({ text: `Side: ${nearbyTrade.side.toUpperCase()}` });
        lines.push({ text: `Amount: ${formatCurrency(nearbyTrade.amount)}` });
    }

    // Volume Section
    if (volumeVisibility.showVolume && volumePoint) {
        lines.push({ text: '', isSeparator: true });
        lines.push({ text: '-- Volume --', isHeader: true });

        if (volumeVisibility.metric === 'volume') {
            lines.push({ text: `Total: ${formatCurrency(volumePoint.totalVolume)}` });
        } else if (volumeVisibility.metric === 'tradeCount') {
            lines.push({ text: `Trades: ${volumePoint.tradeCount}` });
        } else if (volumeVisibility.metric === 'buySellVolume') {
            lines.push({ text: `Buy: ${formatCurrency(volumePoint.buyVolume)}` });
            lines.push({ text: `Sell: ${formatCurrency(volumePoint.sellVolume)}` });
        }
    }

    return { lines, nearbyTrade };
}

export function renderFullSeriesTooltip(
    textElement: d3.Selection<SVGTextElement, unknown, null, undefined>,
    lines: TooltipLine[]
): { maxWidth: number } {
    textElement.selectAll('tspan').remove();

    let lineIndex = 0;
    let maxWidth = 0;

    lines.forEach((line) => {
        if (line.isSeparator) {
            lineIndex++;
            return;
        }

        const tspan = textElement.append('tspan')
            .attr('x', 8)
            .attr('y', 15 + lineIndex * 14)
            .attr('fill', line.isHeader ? 'rgba(255,255,255,0.7)' : 'white')
            .style('font-size', line.isHeader ? '11px' : '12px')
            .text(line.text);

        const width = (tspan.node() as SVGTSpanElement).getComputedTextLength();
        maxWidth = Math.max(maxWidth, width);
        lineIndex++;
    });

    return { maxWidth };
}

// Render simple market tooltip (label:value format with right-aligned values)
export function renderSimpleMarketTooltip(
    textElement: d3.Selection<SVGTextElement, unknown, null, undefined>,
    tooltipLines: string[]
): void {
    textElement.selectAll('tspan').remove();

    // First pass: measure widths
    let maxLabelWidth = 0;
    let maxValueWidth = 0;

    tooltipLines.forEach((line, i) => {
        if (line.includes(':')) {
            const [label, value] = line.split(':').map(s => s.trim());

            const tempLabel = textElement.append('tspan')
                .attr('x', 8)
                .attr('y', 15 + i * 14)
                .text(label + ': ');

            const tempValue = textElement.append('tspan')
                .attr('x', 0)
                .attr('y', 15 + i * 14)
                .text(value);

            const labelWidth = (tempLabel.node() as SVGTSpanElement).getComputedTextLength();
            const valueWidth = (tempValue.node() as SVGTSpanElement).getComputedTextLength();

            maxLabelWidth = Math.max(maxLabelWidth, labelWidth);
            maxValueWidth = Math.max(maxValueWidth, valueWidth);
        }
    });

    // Second pass: render with calculated positions
    textElement.selectAll('tspan').remove();
    const valueXPosition = 8 + maxLabelWidth + maxValueWidth + 20;

    tooltipLines.forEach((line, i) => {
        if (line.includes(':')) {
            const [label, value] = line.split(':').map(s => s.trim());

            textElement.append('tspan')
                .attr('x', 8)
                .attr('y', 15 + i * 14)
                .text(label + ':');

            textElement.append('tspan')
                .attr('x', valueXPosition)
                .attr('y', 15 + i * 14)
                .attr('text-anchor', 'end')
                .text(value);
        } else {
            textElement.append('tspan')
                .attr('x', 8)
                .attr('y', 15 + i * 14)
                .text(line);
        }
    });
}

// Determine tick format based on time range and uniqueness
export type TickFormat = 'date' | 'date-time' | 'date-time-seconds' | 'time' | 'time-seconds';

export function getTickFormat(timeDomain: [Date, Date], ticks: Date[]): TickFormat {
    const rangeMs = timeDomain[1].getTime() - timeDomain[0].getTime();
    const hoursInRange = rangeMs / (1000 * 60 * 60);
    const includeDate = hoursInRange >= 24;

    if (includeDate) {
        const dateStrings = ticks.map(d =>
            d.toLocaleDateString('de-DE', { month: 'short', day: 'numeric' })
        );
        if (new Set(dateStrings).size === dateStrings.length) {
            return 'date';
        }

        const dateTimeStrings = ticks.map(d =>
            d.toLocaleDateString('de-DE', { month: 'short', day: 'numeric' }) + ' ' +
            d3.timeFormat('%H:%M')(d)
        );
        if (new Set(dateTimeStrings).size === dateTimeStrings.length) {
            return 'date-time';
        }

        return 'date-time-seconds';
    }

    const timeStrings = ticks.map(d => d3.timeFormat('%H:%M')(d));
    if (new Set(timeStrings).size === timeStrings.length) {
        return 'time';
    }

    return 'time-seconds';
}
